import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Container, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';
import { setSelectedMatch, fetchFullEvent, fetchBetBuilderAvailableSelections } from 'stores/match/index.js';
import { setBetBuilderEnabled, clearBetBuilderSelections, calculateBetBuilderOdds } from 'stores/betSlip';
import { showToaster } from 'mixin/general';
import { BetGroups, getBetGroups, BET_BUILDER_ENABLED } from 'toolkit/constants.js';
import BetBuilderModal from './bet_builder_modal';
import { isMarketLineOfferedForBetBuilder } from 'toolkit/betBuilder';
import {
  MarketIdToMarketName,
  ReverseName,
  checkHasMarketVariable,
  getLanguageCode,
  gameDateToMatchDayFormatted,
  sportTypeToSportId,
  isHandicap90Specifier
} from 'toolkit/utils';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { getMatchTime } from 'toolkit/utils.js';
import { EnumMarketName, EnumEventStatus } from 'toolkit/Enums.js';
import { getMatchDetail, getTeamNameHelper } from 'helper/matchDetailHelper';
import MatchTracker from '../match_tracker/match_tracker.js';
import MatchStreamer from '../match_tracker/match_streamer.js';
import Statistics from '../match_tracker/statistics/statistics.js';
import BetBox from './bet_box';
import BetTitleAndBox from './bet_title_and_box.js';
import { useMediaQuery } from 'react-responsive';
import { EBetGroupName } from 'toolkit/Enums.js';
import Jersey from '../jersey/index.js';

function MatchDetail({ matchDetailId, onMatchDetailIdSet }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const location = useLocation();
  const navigate = useNavigate();
  const providerId = matchDetailId || location.hash.slice(1);
  const match = useSelector((state) => state.match.events[providerId]);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const [matchDetail, setMatchDetail] = useState(undefined);
  const [availableMarketGroups, setAvailableMarketGroups] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(EBetGroupName.TOP_BETS);
  const isDesktop = useMediaQuery({
    query: '(min-width: 992px)'
  });
  const [matchtrackerCallback, setMatchtrackerCallback] = useState(null);
  const [isStreamView, setIsStreamView] = useState(false);
  const [currentView, setCurrentView] = useState('tracker');
  const [streamError, setStreamError] = useState(false);
  const [streamAvailable, setStreamAvailable] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');
  const [fullscreenUrl, setFullscreenUrl] = useState('');
  const betBuilder = useSelector((state) => state.betSlip.betBuilder);
  const betSlip = useSelector((state) => state.betSlip);
  const betBuilderAvailableSelections = useSelector((state) => state.match.betBuilderAvailableSelections[providerId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMatch();

    // Disable bet builder mode when leaving match detail
    return () => {
      dispatch(setBetBuilderEnabled(false));
      dispatch(clearBetBuilderSelections());
    };
  }, []);

  // Reset match detail to initial state when bet builder is disabled
  useEffect(() => {
    if (!betBuilder.isEnabled) {
      setSelectedMarket(EBetGroupName.TOP_BETS);
      setCurrentView('tracker');
      setIsStreamView(false);
      setStreamError(false);
    }
  }, [betBuilder.isEnabled]);

  function fetchMatch() {
    dispatch(setSelectedMatch(providerId));
    dispatch(fetchFullEvent(providerId))
      .unwrap()
      .then((response) => {
        if (!response) {
          navigate('/live');
        }
      });
    // Fetch bet builder available selections for this match
    dispatch(fetchBetBuilderAvailableSelections(providerId));
  }

  useEffect(() => {
    if (match) {
      setMatchDetail(getMatchDetail(match));
      // Check if stream is available when match is loaded
      checkStreamAvailability();
    }
  }, [match]);

  // Bet Builder Icon Component
  const BetBuilderIcon = ({ size = 24, style = {}, firstBColor = '#000000' }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '6px', verticalAlign: 'middle', cursor: 'pointer', ...style }}
      title={t('availableForBetBuilder')}
    >
      <g transform="skewX(-14)">
        {/* First B (configurable color) */}
        <text
          x="6"
          y="46"
          fontSize="42"
          fontWeight="900"
          fontFamily="Arial Black, Arial, sans-serif"
          fill={firstBColor}
          letterSpacing="-5"
        >
          B
        </text>
        {/* Red B */}
        <text
          x="30"
          y="46"
          fontSize="42"
          fontWeight="900"
          fontFamily="Arial Black, Arial, sans-serif"
          fill="#E10600"
          letterSpacing="-5"
        >
          B
        </text>
      </g>
    </svg>
  );

  // Check if bet builder is available for this match
  const isBetBuilderAvailable = () => {
    // Check global feature flag first
    if (!BET_BUILDER_ENABLED) return false;

    if (!betBuilderAvailableSelections) return false;

    // Check if the response has the expected structure with success and data
    if (betBuilderAvailableSelections.success && betBuilderAvailableSelections.data) {
      const { markets } = betBuilderAvailableSelections.data;
      return Array.isArray(markets) && markets.length > 0;
    }

    return false;
  };

  // Get available market IDs for bet builder
  const getBetBuilderAvailableMarketIds = () => {
    if (!isBetBuilderAvailable()) return [];

    const { markets } = betBuilderAvailableSelections.data;
    return markets.map(market => market.marketId);
  };

  // Check if a specific market is available for bet builder
  const isMarketAvailableForBetBuilder = (marketId) => {
    if (!isBetBuilderAvailable()) return false;
    const availableMarketIds = getBetBuilderAvailableMarketIds();
    return availableMarketIds.includes(marketId);
  };

  // Toggle bet builder mode
  const handleBetBuilderToggle = () => {
    const willEnable = !betBuilder.isEnabled;
    if (willEnable) {
      // Check if bet builder is available for this match
      if (!isBetBuilderAvailable()) {
        return;
      }
      // Check if there are normal bets for this event before enabling bet builder
      const hasNormalBet = betSlip.bets.some(
        (b) => b.providerId === providerId && !b.isBetBuilder
      );
      if (hasNormalBet) {
        showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
        return;
      }
      dispatch(setBetBuilderEnabled(true));
    } else {
      // Disable bet builder
      dispatch(setBetBuilderEnabled(false));
      dispatch(clearBetBuilderSelections());
    }
  };

  // Reset match detail to initial state when bet builder is disabled
  useEffect(() => {
    if (!betBuilder.isEnabled) {
      setSelectedMarket(EBetGroupName.TOP_BETS);
    }
  }, [betBuilder.isEnabled]);

  // Calculate bet builder odds when selections change; quick successive clicks send one request
  useEffect(() => {
    if (!betBuilder.isEnabled || betBuilder.selections.length === 0) return;
    const timer = setTimeout(() => dispatch(calculateBetBuilderOdds(betBuilder.selections)), 300);
    return () => clearTimeout(timer);
  }, [betBuilder.selections, betBuilder.isEnabled, dispatch]);

  // Debug logging for bet builder
  useEffect(() => {
  }, [betBuilderAvailableSelections, betBuilder.isEnabled]);

  function checkStreamAvailability() {
    if (!match || !match.providerId) return;

    const matchId = match.providerId.split(':')[2];
    const url = `https://geturl.yextof.com/bradar?bradar=${matchId}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setStreamAvailable(false);
          setStreamUrl('');
          setFullscreenUrl('');
        } else if (data.fullscreen && typeof data.fullscreen === 'string' && data.fullscreen.length > 0) {
          setStreamAvailable(true);
          setFullscreenUrl(data.fullscreen);
          setStreamUrl(data.fullscreen); // Use fullscreen URL in iframe initially
        } else if (data.url && typeof data.url === 'string' && data.url.length > 0) {
          setStreamAvailable(true);
          setStreamUrl(data.url);
          setFullscreenUrl(''); // Clear fullscreen URL when only regular URL is available
        } else {
          setStreamAvailable(false);
          setStreamUrl('');
          setFullscreenUrl('');
        }
      })
      .catch((error) => {
        console.error('Error checking stream availability:', error);
        setStreamAvailable(false);
        setStreamUrl('');
      });
  }

  useEffect(() => {
    getMarketGroups();
  }, [match, betBuilder.isEnabled, betBuilderAvailableSelections]);

  function getMarketGroups() {
    if (match === undefined) return;

    // Filter markets based on bet builder mode
    let marketsToProcess = match.markets;

    // In bet builder mode only the market lines MyOdds offers (e.g. Total 2.5, not every total line)
    if (betBuilder.isEnabled && isBetBuilderAvailable()) {
      const offeredMarkets = betBuilderAvailableSelections.data.markets;
      marketsToProcess = match.markets.filter((m) => isMarketLineOfferedForBetBuilder(m, offeredMarkets));
    }

    const tempAvailableTabOrders = new Map();
    marketsToProcess.forEach((m) => {
      const betGroupNames = getBetGroups(m.id) ?? [EBetGroupName.SPECIAL_BETS];
      betGroupNames.map((betGroupName) => {
        if (!tempAvailableTabOrders.has(betGroupName)) {
          tempAvailableTabOrders.set(betGroupName, BetGroups[betGroupName].tabOrder);
        }
      });
    });

    setAvailableMarketGroups(
      Array.from(tempAvailableTabOrders.entries())
        .sort(([, tabOrderA], [, tabOrderB]) => tabOrderA - tabOrderB)
        .map(([name]) => {
          return name;
        })
    );

    if (tempAvailableTabOrders.length == 1) {
      setSelectedMarket(tempAvailableTabOrders[0]);
    }
  }

  function getGoalScorers(outcomeId, marketId) {
    const market = match.markets.find((x) => x.id === marketId);
    if (market) {
      return (
        <Col xs={2} className="m-0 p-1">
          <BetBox
            key={outcomeId}
            providerId={providerId}
            outcomeId={outcomeId}
            marketStringId={market.stringId}
            showOddTitle={false}
          />
        </Col>
      );
    } else {
      return (
        <Col xs={2} className="m-0 p-1">
          <BetBox
            key={outcomeId}
            providerId={providerId}
            outcomeId={outcomeId}
            marketStringId={null}
            showOddTitle={false}
          />
        </Col>
      );
    }
  }

  const sortByMarketId = (arr) => {
    return _.orderBy(arr, (market) => {
      const marketVariable = checkHasMarketVariable(market.id, market.stringId);
      if (marketVariable) {
        if (!isNaN(Number(marketVariable))) return Number(marketVariable);
        return marketVariable;
      }
      return false;
    });
  };

  function getMarkets(betGroupName) {
    // Filter markets based on bet builder mode
    let allMarkets = match.markets;

    if (betBuilder.isEnabled && isBetBuilderAvailable()) {
      const offeredMarkets = betBuilderAvailableSelections.data.markets;
      allMarkets = match.markets.filter((m) => isMarketLineOfferedForBetBuilder(m, offeredMarkets));
    }

    let markets;
    if (betGroupName === EBetGroupName.SPECIAL_BETS) {
      const includedBetId = Object.values(BetGroups).flatMap((group) => group.markets);
      const notIncludedBets = allMarkets.filter((item) => !includedBetId.includes(item.id));
      markets = [
        ...allMarkets
          .filter((item) => BetGroups[betGroupName].markets.includes(item.id))
          .sort((t1, t2) => {
            return (
              BetGroups[EBetGroupName.SPECIAL_BETS].markets.indexOf(t1.id) -
              BetGroups[EBetGroupName.SPECIAL_BETS].markets.indexOf(t2.id)
            );
          }),
        ...notIncludedBets
      ];
    } else {
      markets = [
        ...allMarkets
          .filter((item) => BetGroups[betGroupName].markets.includes(item.id))
          .map((m) => ({ ...m, outcomes: [...m.outcomes] }))
      ];
    }
    const marketList = [];

    // Top Bets Markets Groups
    if (betGroupName === EBetGroupName.TOP_BETS) {
      const correctScore = [];
      const overUnderTopBets = [];
      const handicapTopBets90 = [];
      const handicapTopBets = [];
      const goalRangeTopBets = [];
      const winnerTopBets = [];
      const winnerEarlyPayoutTopBets = [];
      const nextGoalTopBets = [];
      const bothTeamToScoreTopBets = [];
      const otherList = [];
      markets = markets.sort((t1, t2) => t1.id - t2.id);

      const marketRom = markets.find((x) => x.id == EnumMarketName.ROM_7);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBuilderIcon={isMarketAvailableForBetBuilder(marketRom.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
            />
          </div>
        );
      }
      if (match.sport == sportTypeToSportId('Football')) {
        const sortedHandicapTopBetMarkets = sortByMarketId(
          markets.filter((market) => market.id == EnumMarketName.HANDICAP_14)
        );
        let handicapTopBetIndex = 0;
        markets = _.orderBy(markets, (market) => {
          if (market.id == EnumMarketName.TOTAL_18)
            var data = market.stringId.split(':')[1]
          //if (data === '0') {
          //data = market.stringId.split(':')[2];
          //}
          return data;
        });
        markets.forEach((market) => {
          if (market.id != EnumMarketName.ROM_7) {
            let targetArr;
            if (market.id == EnumMarketName.TOTAL_18) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              targetArr = overUnderTopBets;
              const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                />
              );
              if (targetArr.length == 0) {
                targetArr.push(
                  <div
                    className={`text-left fs-7 over-under ${isDesktop ? 'text-white' : ''}`}
                    key={'bet_name' + market.stringId + 'top'}>
                    <span className="d-inline-flex align-items-center">
                      {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                      {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
                    </span>
                  </div>
                );
              }
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.HANDICAP_14) {
              const handicapMarket = sortedHandicapTopBetMarkets[handicapTopBetIndex++] || market;
              const targetArr = isHandicap90Specifier(handicapMarket.stringId)
                ? handicapTopBets90
                : handicapTopBets;
              const betItem = (
                <BetTitleAndBox
                  key={handicapMarket.stringId + 'tophan'}
                  market={handicapMarket}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(handicapMarket.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              if (targetArr.length == 0) {
                targetArr.push(
                  <div
                    className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
                    key={'bet_name' + handicapMarket.stringId + 'tophan'}>
                    {targetArr === handicapTopBets90
                      ? t('pages.start.titleMaradonaHandicap')
                      : MarketIdToMarketName(handicapMarket.id, handicapMarket.stringId).split(' + ')[0]}
                  </div>
                );
              }

              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.GOALRANGE_25) {
              targetArr = goalRangeTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              if (targetArr.length == 0) {
                targetArr.push(
                  <div
                    className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
                    key={'bet_name' + market.stringId}>
                    {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                  </div>
                );
              }

              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.NEXTGOAL_8) {
              targetArr = nextGoalTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.WINNER_1) {
              targetArr = winnerTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.BOTHTEAMSTOSCORE_29) {
              targetArr = bothTeamToScoreTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.WINNEREARLYPAYOUT_1601) {
              targetArr = winnerEarlyPayoutTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else {
              targetArr = otherList;
              targetArr.push(
                <div
                  className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                    }`}
                  key={
                    'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
                  }>
                  <BetTitleAndBox
                    key={market.stringId}
                    market={market}
                    matchId={providerId}
                    matchDetail={matchDetail}
                    sportType={match.sport}
                    betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                  />
                </div>
              );
            }
          }
        });

        if (winnerTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winner'}>
              {winnerTopBets}
            </div>
          );
        }

        if (winnerEarlyPayoutTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winnerEarlyPayout'}>
              {winnerEarlyPayoutTopBets}
            </div>
          );
        }

        if (nextGoalTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-nextGoal'}>
              {nextGoalTopBets}
            </div>
          );
        }

        if (bothTeamToScoreTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-bothTeamToScore'}>
              {bothTeamToScoreTopBets}
            </div>
          );
        }

        if (overUnderTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-overUnder'}>
              {overUnderTopBets}
            </div>
          );
        }

        if (handicapTopBets90.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-handicap90'}>
              {handicapTopBets90}
            </div>
          );
        }

        if (handicapTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-handiCap'}>
              {handicapTopBets}
            </div>
          );
        }

        if (otherList.length > 0) {
          marketList.push(
            <div key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-otherList'}>
              {otherList}
            </div>
          );
        }

        if (goalRangeTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-goalRange'}>
              {goalRangeTopBets}
            </div>
          );
        }

        if (correctScore.length > 0) {
          marketList.push(
            <div key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-correctScore'}>
              {correctScore}
            </div>
          );
        }

        return marketList;
      } else {
        const firstHalfOverUnderList = [];
        const winnerTopBets = [];
        const overUnderList = [];
        const firstHalfHandicap = [];
        const handicapList = [];
        const overUnderGamesList = [];
        const overUnderSetList = [];
        const gameHandicapList = [];
        const quarterOverUnderList = [];
        const quarterWinner = [];
        const quarterHandicap = [];
        const lastMarkets = [];
        const secondHalfHandicapList = [];
        markets = _.orderBy(markets, (market) => {
          if (
            market.id == EnumMarketName.TOTAL_18 ||
            market.id == EnumMarketName.HOMETOTAL_19 ||
            market.id == EnumMarketName.AWAYTOTAL_20 ||
            market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
            market.id == EnumMarketName.TOTAL_225 ||
            market.id == EnumMarketName.QUARTEROVERUNDER_236 ||
            market.id == EnumMarketName.QUARTERWINNER_235
          ) {
            return market.stringId.split(':')[1];
          } else if (market.id === EnumMarketName.QUARTERHANDICAP_303) {
            return market.stringId.split(':')[2];
          }
        });
        //markets = sortByMarketId(markets);
        markets.forEach((market) => {
          let targetArr;
          if (market.id != EnumMarketName.ROM_7) {
            let checkArrLength = true;
            let betItem = (
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
              />
            );
            if (
              market.id == EnumMarketName.TOTAL_18 ||
              market.id == EnumMarketName.TOTAL_225 ||
              market.id == EnumMarketName.TOTALPOINT_238 ||
              market.id == EnumMarketName.TOTAL_258 ||
              market.id == EnumMarketName.TOTALFRAMES_494
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              targetArr = overUnderList;
            } else if (market.id === EnumMarketName.WINNER_186) {
              checkArrLength = false;
              targetArr = winnerTopBets;
            } else if (market.id == EnumMarketName.FIRSTHALFHANDICAP_66) {
              targetArr = firstHalfHandicap;
            } else if (market.id == EnumMarketName.WINNER_219) {
              checkArrLength = false;
              targetArr = correctScore;
            } else if (market.id == EnumMarketName.FIRSTHALFOVERUNDER_68) {
              targetArr = firstHalfOverUnderList;
            } else if (market.id == EnumMarketName.TOTALGAMESSET_204) {
              targetArr = overUnderSetList;
            } else if (market.id == EnumMarketName.TOTALGAMES_189) {
              targetArr = overUnderGamesList;
            } else if (market.id == EnumMarketName.QUARTERHANDICAP_303) {
              checkArrLength = false;
              targetArr = quarterHandicap;
            } else if (
              market.id == EnumMarketName.HANDICAP_14 ||
              market.id == EnumMarketName.HANDICAP_16 ||
              market.id == EnumMarketName.GAMEHANDICAP_187 ||
              market.id == EnumMarketName.HANDICAP_223 ||
              market.id == EnumMarketName.HANDICAP_256 ||
              market.id == EnumMarketName.POINTHANDICAP_237 ||
              market.id == EnumMarketName.FRAMEHANDICAP_493
            ) {
              targetArr = handicapList;
            } else if (market.id == EnumMarketName.QUARTERWINNER_235) {
              checkArrLength = false;
              targetArr = quarterWinner;
            } else if (market.id == EnumMarketName.QUARTEROVERUNDER_236) {
              targetArr = quarterOverUnderList;
              checkArrLength = false;
            } else {
              checkArrLength = false;
              targetArr = lastMarkets;
              betItem = (
                <div
                  className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                    }`}
                  key={
                    'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
                  }>
                  {betItem}
                </div>
              );
            }
            if (checkArrLength && targetArr.length == 0) {
              const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
              targetArr.push(
                <div
                  className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
                  key={'bet_name' + market.stringId}>
                  <span className="d-inline-flex align-items-center">
                    {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                    {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
                  </span>
                </div>
              );
            }
            targetArr.push(betItem);
          }
        });

        if (winnerTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winner'}>
              {winnerTopBets}
            </div>
          );
        }
        if (correctScore.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-correctScore'}>
              {correctScore}
            </div>
          );
        if (firstHalfHandicap.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-firstHalfHandicap'}>
              {firstHalfHandicap}
            </div>
          );
        if (handicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-handicap'}>
              {handicapList}
            </div>
          );
        if (overUnderList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-overUnder'}>
              {overUnderList}
            </div>
          );

        if (quarterWinner.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-quarterWinner'}>
              {quarterWinner}
            </div>
          );
        }
        if (quarterHandicap.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winner'}>
              {quarterHandicap}
            </div>
          );
        }

        if (quarterOverUnderList.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-quarterOverUnder'}>
              {quarterOverUnderList}
            </div>
          );
        }
        if (firstHalfOverUnderList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-firstHalfOverUnder'
              }>
              {firstHalfOverUnderList}
            </div>
          );
        if (overUnderGamesList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-overUnderGames'}>
              {overUnderGamesList}
            </div>
          );
        if (gameHandicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
              {gameHandicapList}
            </div>
          );
        if (overUnderSetList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
              {overUnderSetList}
            </div>
          );
        if (secondHalfHandicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
              {secondHalfHandicapList}
            </div>
          );
        if (lastMarkets.length > 0) marketList.push(lastMarkets);
        return marketList;
      }
    }

    // Over Under Markets Groups
    if (betGroupName == EBetGroupName.Over_Under) {
      const overUnderList = [];
      const homeOverUnderList = [];
      const awayOverUnderList = [];
      const firstHalfOverUnderList = [];
      const overUnderGamesList = [];
      const otherList = [];
      const firstHalfAwayOverUnderList = [];
      const firstHalfHomeOverUnderList = [];
      const secondHalfOverUnderList = [];
      const secondHalfHomeOverUnderList = [];
      const secondHalfAwayOverUnderList = [];
      const homeAnotherUnderList = [];
      const awayAnotherUnderList = [];
      const secondHalfTotallist = [];
      markets = _.orderBy(markets, (market) => {
        if (
          market.id == EnumMarketName.TOTAL_18 ||
          market.id == EnumMarketName.HOMETOTAL_19 ||
          market.id == EnumMarketName.AWAYTOTAL_20 ||
          market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
          market.id == EnumMarketName.TOTAL_225 ||
          market.id == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69 ||
          market.id == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70 ||
          market.id == EnumMarketName.SECONDHALFOVERUNDER_90 ||
          market.id == EnumMarketName.SECONDHALFHOMEOVERUNDER_91 ||
          market.id == EnumMarketName.SECONDHALFAWAYOVERUNDER_92 ||
          market.id == EnumMarketName.HOMEOVERUNDER_227 ||
          market.id == EnumMarketName.AWAYOVERUNDER_228 ||
          market.id == EnumMarketName.SECONDHALFTOTAL_232 ||
          market.id == EnumMarketName.HOMEOVERUNDER_756 ||
          market.id == EnumMarketName.AWAYOVERUNDER_757
        ) {
          return market.stringId.split(':')[1];
        }
      });
      markets.forEach((market) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        );
        if (
          marketId == EnumMarketName.TOTAL_18 ||
          marketId == EnumMarketName.TOTAL_225 ||
          marketId == EnumMarketName.TOTALPOINT_238 ||
          marketId == EnumMarketName.TOTAL_258 ||
          marketId == EnumMarketName.TOTALFRAMES_494
        ) {
          market.outcomes = _.orderBy(market?.outcomes, ['id'], ['asc']);
          targetArr = overUnderList;
        } else if (
          marketId == EnumMarketName.HOMETOTAL_19 ||
          marketId === EnumMarketName.HOMEOVERUNDER_756
        ) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = homeOverUnderList;
        } else if (
          marketId == EnumMarketName.AWAYTOTAL_20 ||
          marketId === EnumMarketName.AWAYOVERUNDER_757
        ) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = awayOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFOVERUNDER_68) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfHomeOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfAwayOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFOVERUNDER_90) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFTOTAL_232) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfTotallist;
        } else if (marketId == EnumMarketName.SECONDHALFHOMEOVERUNDER_91) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfHomeOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFAWAYOVERUNDER_92) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfAwayOverUnderList;
        } else if (marketId == EnumMarketName.TOTALGAMES_189) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = overUnderGamesList;
        } else if (marketId == EnumMarketName.HOMEOVERUNDER_227) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = homeAnotherUnderList;
        } else if (marketId == EnumMarketName.AWAYOVERUNDER_228) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = awayAnotherUnderList;
        } else {
          targetArr = otherList;
          checkArrLength = false;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
              }>
              {betItem}
            </div>
          );
        }

        if (checkArrLength && targetArr.length == 0) {
          const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
          targetArr.push(
            <div
              className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
              key={'bet_name' + market.stringId}>
              <span className="d-inline-flex align-items-center">
                {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
              </span>
            </div>
          );
        }

        targetArr.push(betItem);
      });

      if (overUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-overUnder'}>
            {overUnderList}
          </div>
        );
      if (homeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-homeOverUnder'}>
            {homeOverUnderList}
          </div>
        );
      if (awayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-awayOverUnder'}>
            {awayOverUnderList}
          </div>
        );
      if (homeAnotherUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={
              'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-homeAnotherOverUnder'
            }>
            {homeAnotherUnderList}
          </div>
        );
      if (awayAnotherUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={
              'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-awayAnotherUnderList'
            }>
            {awayAnotherUnderList}
          </div>
        );
      if (firstHalfOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfOverUnder'}>
            {firstHalfOverUnderList}
          </div>
        );
      if (firstHalfAwayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfAwayOverUnderList'}>
            {firstHalfAwayOverUnderList}
          </div>
        );

      if (firstHalfHomeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfHomeOverUnderList'}>
            {firstHalfHomeOverUnderList}
          </div>
        );
      if (secondHalfOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfOverUnderList'}>
            {secondHalfOverUnderList}
          </div>
        );
      if (secondHalfTotallist.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfOverUnderList'}>
            {secondHalfTotallist}
          </div>
        );
      if (secondHalfHomeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfHomeOverUnderList'}>
            {secondHalfHomeOverUnderList}
          </div>
        );
      if (secondHalfAwayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfAwayOverUnderList'}>
            {secondHalfAwayOverUnderList}
          </div>
        );
      if (overUnderGamesList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-overUnderGames'}>
            {overUnderGamesList}
          </div>
        );
      if (otherList.length > 0) marketList.push(otherList);

      return marketList;
    }
    // 1st Halftime Markets Group
    if (betGroupName == EBetGroupName.FIRST_HALF_TIME) {
      const firstHalfHandicap = [];
      const firstHalfOtherHandicap = [];
      const firstHalfOverUnder = [];
      const firstHalfHomeOverUnder = [];
      const firstHalfAwayOverUnder = [];
      const firstHalfExactGoals = [];
      const multiGoals = [];
      const firstBothTeamsToScore = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.FIRSTHALFEXACTGOALS_71) {
          return market.stringId.split(':')[3];
        }
        if (
          market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
          market.id == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69 ||
          market.id == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70
        )
          return market.stringId.split(':')[1];
      });

      markets = sortByMarketId(markets);
      markets = markets.sort((t1, t2) => t1.id - t2.id);
      const marketRom = markets.find((x) => x.id == 61);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
            />
          </div>
        );
      }

      markets.forEach((market) => {
        if (market.id != EnumMarketName.FIRSTHALFROM_61) {
          const marketId = market.id;
          let targetArr;
          let checkArrLength = true;
          let betItem = (
            <BetTitleAndBox
              key={market.stringId}
              market={market}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
            />
          );
          if (marketId == EnumMarketName.FIRSTHALFHANDICAP_65) {
            targetArr = firstHalfHandicap;
          } else if (marketId == EnumMarketName.FIRSTHALFHANDICAP_66) {
            targetArr = firstHalfOtherHandicap;
          } else if (marketId == EnumMarketName.FIRSTHALFOVERUNDER_68) {
            market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            targetArr = firstHalfOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69) {
            targetArr = firstHalfHomeOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70) {
            targetArr = firstHalfAwayOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFEXACTGOALS_71) {
            targetArr = firstHalfExactGoals;
          } else if (marketId == EnumMarketName.FIRSTHALFMULTIGOALS_552) {
            targetArr = multiGoals;
          } else if (marketId == EnumMarketName.FIRSTHALFBOTHTEAMSTOSCORE_75) {
            targetArr = firstBothTeamsToScore;
          } else {
            if (
              marketId == EnumMarketName.FIRSTHALFNEXTGOAL_62 ||
              marketId == EnumMarketName.FIRSTHALFROM_61
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            }
            checkArrLength = false;
            targetArr = marketList;
            betItem = (
              <div
                className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                  }`}
                key={
                  'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
                }>
                {betItem}
              </div>
            );
          }
          if (checkArrLength && targetArr.length == 0) {
            const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
            targetArr.push(
              <div
                className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
                key={'bet_name' + market.stringId}>
                <span className="d-inline-flex align-items-center">
                  {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                  {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
                </span>
              </div>
            );
          }
          targetArr.push(betItem);
        }
      });

      if (firstHalfHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfHandicap}
          </div>
        );
      if (firstHalfOtherHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfOtherHandicap}
          </div>
        );
      if (firstHalfOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfOverUnder'}>
            {firstHalfOverUnder}
          </div>
        );
      if (firstHalfHomeOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfHomeOverUnder'}>
            {firstHalfHomeOverUnder}
          </div>
        );
      if (firstHalfAwayOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfAwayOverUnder'}>
            {firstHalfAwayOverUnder}
          </div>
        );
      if (firstHalfExactGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstHalfExactGoals'}>
            {firstHalfExactGoals}
          </div>
        );
      if (firstBothTeamsToScore.length > 0) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-firstBothTeamsToScore'}>
            {firstBothTeamsToScore}
          </div>
        );
      }
      if (multiGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}>
            {multiGoals}
          </div>
        );
      return marketList;
    }
    //Second Halftimer Markets Group
    if (betGroupName === EBetGroupName.SECOND_HALF_TIME) {
      const secondHalfOverUnder = [];
      const secondHalfHomeOverUnder = [];
      const secondHalfAwayOverUnder = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.SECONDHALFNEXTGOAL_84) {
          return market.stringId.split(':')[3];
        }
        if (
          market.id == EnumMarketName.SECONDHALFOVERUNDER_90 ||
          market.id == EnumMarketName.SECONDHALFHOMEOVERUNDER_91 ||
          market.id == EnumMarketName.SECONDHALFAWAYOVERUNDER_92
        )
          return market.stringId.split(':')[1];
      });

      markets = sortByMarketId(markets);
      markets = markets.sort((t1, t2) => t1.id - t2.id);
      const marketRom = markets.find((x) => x.id == 83);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
            />
          </div>
        );
      }

      markets.forEach((market) => {
        if (market.id != EnumMarketName.SECONDHALFWINNER_83) {
          const marketId = market.id;
          let targetArr;
          let checkArrLength = true;
          let betItem = (
            <BetTitleAndBox
              key={market.stringId}
              market={market}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
            />
          );
          if (marketId == EnumMarketName.SECONDHALFOVERUNDER_90) {
            market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            targetArr = secondHalfOverUnder;
          } else if (marketId == EnumMarketName.SECONDHALFHOMEOVERUNDER_91) {
            targetArr = secondHalfHomeOverUnder;
          } else if (marketId == EnumMarketName.SECONDHALFAWAYOVERUNDER_92) {
            targetArr = secondHalfAwayOverUnder;
          } else {
            if (
              marketId == EnumMarketName.SECONDHALFNEXTGOAL_84 ||
              marketId == EnumMarketName.SECONDHALFWINNER_83
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            }
            checkArrLength = false;
            targetArr = marketList;
            betItem = (
              <div
                className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                  }`}
                key={
                  'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
                }>
                {betItem}
              </div>
            );
          }
          if (checkArrLength && targetArr.length == 0) {
            const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
            targetArr.push(
              <div
                className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
                key={'bet_name' + market.stringId}>
                <span className="d-inline-flex align-items-center">
                  {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                  {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
                </span>
              </div>
            );
          }
          targetArr.push(betItem);
        }
      });

      if (secondHalfOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfOverUnder'}>
            {secondHalfOverUnder}
          </div>
        );
      if (secondHalfHomeOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondHalfHomeOverUnder'}>
            {secondHalfHomeOverUnder}
          </div>
        );
      if (secondHalfAwayOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'market-list-secondtHalfAwayOverUnder'}>
            {secondHalfAwayOverUnder}
          </div>
        );
      return marketList;
    }
    // Handicap Markets Group
    if (betGroupName == EBetGroupName.HANDICAP) {
      const handicapList90 = [];
      const handicapList = [];
      const firstHalfHandicap = [];
      const gameHandicap = [];
      const quarterHandicap = [];
      const setHandicapList = [];
      const secondHalfHandicapList = [];
      markets = sortByMarketId(markets);
      markets.forEach((market) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        );
        if (
          marketId == EnumMarketName.HANDICAP_14 &&
          isHandicap90Specifier(market.stringId)
        ) {
          targetArr = handicapList90;
        } else if (
          marketId == EnumMarketName.HANDICAP_14 ||
          marketId == EnumMarketName.HANDICAP_16 ||
          marketId == EnumMarketName.GAMEHANDICAP_187 ||
          marketId == EnumMarketName.HANDICAP_223 ||
          marketId == EnumMarketName.HANDICAP_256 ||
          marketId == EnumMarketName.POINTHANDICAP_237 ||
          marketId == EnumMarketName.FRAMEHANDICAP_493
        ) {
          targetArr = handicapList;
        } else if (market.id == EnumMarketName.SETGAMEHANDICAP_203) {
          targetArr = setHandicapList;
        } else if (
          marketId == EnumMarketName.FIRSTHALFHANDICAP_65 ||
          marketId == EnumMarketName.FIRSTHALFHANDICAP_66
        ) {
          targetArr = firstHalfHandicap;
        } else if (marketId == EnumMarketName.QUARTERHANDICAP_303) {
          checkArrLength = false;
          targetArr = quarterHandicap;
        } else if (marketId == EnumMarketName.gameHandicap) {
          targetArr = gameHandicap;
        } else if (marketId == EnumMarketName.SECONDHALFHANDICAP_231) {
          targetArr = secondHalfHandicapList;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
              }>
              {betItem}
            </div>
          );
        }

        if (checkArrLength && targetArr.length == 0) {
          const isBbAvailable = isMarketAvailableForBetBuilder(market.id);
          targetArr.push(
            <div
              className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
              key={'bet_name' + market.stringId}>
              <span className="d-inline-flex align-items-center">
                {targetArr === handicapList90
                  ? t('pages.start.titleMaradonaHandicap')
                  : MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                {isBbAvailable && <BetBuilderIcon firstBColor="#000000" />}
              </span>
            </div>
          );
        }
        targetArr.push(betItem);
      });
      if (handicapList90.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-handicap90'}>
            {handicapList90}
          </div>
        );
      if (handicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-handicap'}>
            {handicapList}
          </div>
        );
      if (firstHalfHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfHandicap}
          </div>
        );
      if (quarterHandicap.length > 0) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winner'}>
            {quarterHandicap}
          </div>
        );
      }
      if (gameHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
            {gameHandicap}
          </div>
        );
      if (setHandicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
            {setHandicapList}
          </div>
        );
      if (secondHalfHandicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-gameHandicap'}>
            {secondHalfHandicapList}
          </div>
        );
      return marketList;
    }
    // Corner Markets group
    if (betGroupName === EBetGroupName.CORNER) {
      const cornerOverUnder = [];
      markets = sortByMarketId(markets);
      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={index + market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        );
        if (marketId == 166) {
          targetArr = cornerOverUnder;
        } else {
          targetArr = marketList;
          checkArrLength = false;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' +
                market.stringId +
                index
              }>
              {betItem}
            </div>
          );
        }
        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div
              className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
              key={'bet_name' + market.stringId + index}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
            </div>
          );
        }
        targetArr.push(betItem);
      });
      if (cornerOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-cornerOverUnder'}>
            {cornerOverUnder}
          </div>
        );
      return marketList;
    }
    // Goals Markets Group
    if (betGroupName === EBetGroupName.GOALS) {
      const exactGoals = [];
      const goalRange = [];
      const multiScores = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.EXACTGOALS_21 || market.id == EnumMarketName.GOALRANGE_25) {
          return market.stringId.split(':')[3];
        }
      }).filter((market) => BetGroups[EBetGroupName.GOALS].markets.includes(market.id));

      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
        let betItem = (
          <BetTitleAndBox
            key={'group_6' + market.stringId + index}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        );
        if (marketId == EnumMarketName.EXACTGOALS_21) {
          targetArr = exactGoals;
        } else if (marketId == EnumMarketName.GOALRANGE_25) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = goalRange;
        } else if (marketId == EnumMarketName.MULTISCORES_551) {
          targetArr = multiScores;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-' + market.stringId
              }>
              {betItem}
            </div>
          );
        }
        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div
              className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
              key={'bet_name' + market.stringId + index}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
            </div>
          );
        }
        targetArr.push(betItem);
      });

      if (exactGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-exactGoals'}>
            {exactGoals}
          </div>
        );
      if (goalRange.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-goalRange'}>
            {goalRange}
          </div>
        );
      if (multiScores.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-multiScores'}>
            {multiScores}
          </div>
        );
      return marketList;
    }
    // Double Markets Group
    if (betGroupName === EBetGroupName.DOUBLE_BETS) {
      const winnerOverUnderList = [];
      const DoubleChanceAndOverUnder = [];

      markets = sortByMarketId(markets);

      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={'group_8' + market.stringId + index}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        );
        if (marketId == EnumMarketName.WINNERANDOVERUNDER_37) {
          targetArr = winnerOverUnderList;
        } else if (marketId == EnumMarketName.DOUBLECHANCEOVERUNDER_547) {
          targetArr = DoubleChanceAndOverUnder;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
                }`}
              key={
                'd-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2--' +
                market.stringId +
                index
              }>
              {betItem}
            </div>
          );
        }
        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div
              className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}
              key={'bet_name' + market.stringId + index}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
            </div>
          );
        }
        if (checkArrLength) {
          targetArr.push(betItem, <hr></hr>);
        } else {
          targetArr.push(betItem);
        }
      });
      if (winnerOverUnderList.length > 0) {
        winnerOverUnderList.pop();
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-winneroverunder'}>
            {winnerOverUnderList}
          </div>
        );
      }
      if (DoubleChanceAndOverUnder.length > 0) {
        DoubleChanceAndOverUnder.pop();
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}
            key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2-doublechance'}>
            {DoubleChanceAndOverUnder}
          </div>
        );
      }
      return marketList;
    }
    // Player Markets Group
    if (betGroupName === EBetGroupName.PLAYER) {
      const goalScorerList = [];
      const anytimeGoalScorer = [];
      const anytimeWinner = [];
      const otherList = [];
      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        if (
          marketId == EnumMarketName.NEXTGOALSCORER_38 ||
          marketId == EnumMarketName.LASTGOALSCORER_39 ||
          marketId == EnumMarketName.ANYTIMEGOALSCORER_40
        ) {
          if (goalScorerList.length == 0) {
            goalScorerList.push(
              <div key={'footballer-name' + index}>
                <Row className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}>
                  <Col xs={6} className={isDesktop ? 'text-white' : ''}>
                    {t('goalScorer')}
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}></Col>
                  <Col xs={2} className={`text-center fs-8 ${isDesktop ? 'text-white' : ''}`}>
                    {t('oddType.first')}
                  </Col>
                  <Col xs={2} className={`text-center fs-8 ${isDesktop ? 'text-white' : ''}`}>
                    {t('oddType.anytime')}
                  </Col>
                  <Col xs={2} className={`text-center fs-8 ${isDesktop ? 'text-white' : ''}`}>
                    {t('oddType.last')}
                  </Col>
                </Row>
                {_.orderBy(market.outcomes, ['odds'], ['asc']).map((outcome) => (
                  <>
                    {outcome.odds == 0 || outcome.odds == 1.0 ? null : (
                      <Container>
                        <Row className="mb-1">
                          <Col xs={6} className={`text-left fs-7 ${isDesktop ? 'text-white' : ''}`}>
                            {ReverseName(outcome.name)}
                          </Col>
                          {getGoalScorers(outcome.id, EnumMarketName.NEXTGOALSCORER_38)}
                          {getGoalScorers(outcome.id, EnumMarketName.ANYTIMEGOALSCORER_40)}
                          {getGoalScorers(outcome.id, EnumMarketName.LASTGOALSCORER_39)}
                        </Row>
                      </Container>
                    )}
                  </>
                ))}
              </div>
            );
          }
        } else if (
          marketId == EnumMarketName.ANYTIMEGOALSCORERWINNER_888 ||
          marketId == EnumMarketName.NEXTGOALSCORERWINNER_891
        ) {
          targetArr =
            marketId == EnumMarketName.ANYTIMEGOALSCORERWINNER_888
              ? anytimeGoalScorer
              : anytimeWinner;

          targetArr.push(
            <div key={'any-time-' + marketId + '' + index}>
              {targetArr.length === 0 && (
                <div className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}>
                  {MarketIdToMarketName(market.id, market.stringId)}
                </div>
              )}
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
              />
            </div>
          );
        } else {
          otherList.push(
            <div key={'other-' + marketId + '' + index}>
              <Row className={`text-left fs-6 ${isDesktop ? 'text-white' : ''}`}>
                {MarketIdToMarketName(market.id, market.stringId)}
              </Row>
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
              />
            </div>
          );
        }
      });
      if (goalScorerList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}>
            {goalScorerList}
          </div>
        );
      if (anytimeGoalScorer.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}>
            {anytimeGoalScorer}
          </div>
        );
      if (anytimeWinner.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}>
            {anytimeWinner}
          </div>
        );
      if (otherList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
              }`}>
            {otherList}
          </div>
        );
      return marketList;
    }

    // if groups not matches any market do this//
    if (betGroupName === EBetGroupName.TRIPLE_BETS) {
      markets = markets.sort(
        (t1, t2) => Number(t1.stringId.split(':')[1]) - Number(t2.stringId.split(':')[1])
      );
    }
    markets.forEach((market) => {
      marketList.push(
        <div
          className={`d-flex flex-column gap-2 rounded p-2 mb-2 ${isDesktop ? 'match-card-desktop' : 'match-card-mobile'
            }`}
          key={'d-flex flex-column gap-2 match-card-mobile rounded p-2 mb-2' + market.stringId}>
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
          />
        </div>
      );
    });
    return marketList;
  }

  function PeriodScoreView() {
    let accumulatedHomeScore = 0;
    let accumulatedAwayScore = 0;
    return match?.periodScores?.map((periodScore, index) => {
      // Ignore for tennis and volleyball
      if (
        match.sport !== sportTypeToSportId('Tennis') &&
        match.sport !== sportTypeToSportId('Volleyball') &&
        index + 1 >= match.periodScores.length
      ) {
        return null;
      }
      if (match.sport == sportTypeToSportId('Basketball')) {
        // Basketball
        accumulatedHomeScore += periodScore.homeScore;
        accumulatedAwayScore += periodScore.awayScore;
        return <div key={index}>{`(${accumulatedHomeScore}:${accumulatedAwayScore})`}</div>;
      }
      return <div key={index}>{`(${periodScore.homeScore}:${periodScore.awayScore})`}</div>;
    });
  }

  if (match === undefined || matchDetail === undefined) return <div />;

  return (
    <>
      <div
        className="fw-bold"
        style={{
          backgroundImage: `url(${AWS_BUCKET_URL}/Icons/122.png)`,
          backgroundSize: 'cover',
          height: 180
        }}>
        <Row>
          <div className="text-start text-white px-5 py-0 mt-2 mb-2">
            <Row>
              <Col xs={10}>
                <div className="d-flex flex-row">
                  <button
                    className="btn p-0"
                    onClick={() => {
                      setTimeout(() => {
                        if (matchDetailId) {
                          onMatchDetailIdSet(null);
                        } else {
                          navigate(-1);
                        }
                      }, 10);
                    }}>
                    <img
                      className="icon-color"
                      width="18"
                      height="25"
                      src={`${AWS_BUCKET_URL}/Icons/backicon-caret.svg`}
                    />
                  </button>

                  <div className="ms-2">
                    <div className="fs-9">
                      {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
                        <Badge pill bg="danger">
                          {t('components.matchcard.live')}
                        </Badge>
                      ) : (
                        <div>{gameDateToMatchDayFormatted(match.gameDate, getLanguageCode())}</div>
                      )}
                    </div>
                    <div className="d-flex flex-row">
                      <div className="pt-1 fs-8">{match.league}</div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <div className="stats"></div>
          </div>
        </Row>
        <Row>
          <Col xs={4}>
            <div className="text-white text-center fs-6 ms-1 d-flex flex-column align-items-center">
              <Jersey jersey={matchDetail.home.jersey} color={'white'} />

              {getTeamNameHelper(locales, selectedLocale, matchDetail.home, t)}
            </div>
          </Col>
          <Col xs={4}>
            <div className="m-auto text-white border border-2 border-info rounded d-flex justify-content-center">
              {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
                getMatchTime(match, t)
              ) : (
                <div className="text-capitalize fs-9">
                  {t(`matchStatus.${match.matchStatusId}`)}
                </div>
              )}
            </div>{' '}
            <div className="text-center text-light fs-9 mt-2 h-0 overflow-visible">
              <PeriodScoreView />
            </div>
          </Col>
          <Col xs={4}>
            <div className="text-white text-center fs-6 me-1 d-flex flex-column align-items-center">
              <Jersey jersey={matchDetail.away.jersey} color={'black'} />

              {getTeamNameHelper(locales, selectedLocale, matchDetail.away, t)}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="text-white text-center fs-6 mt-1">{matchDetail.home.homeScore}</div>
          </Col>
          <Col></Col>
          <Col>
            <div className="text-white text-center fs-6 mt-1">{matchDetail.away.awayScore}</div>
          </Col>
        </Row>
      </div>
      <div>
        <div className="d-flex">
          <div
            className={`flex-fill d-flex flex-column bg-primary align-items-center justify-content-center cursor-pointer ${currentView === 'tracker' ? 'bg-primary' : 'bg-dark'
              }`}
            onClick={() => {
              setCurrentView('tracker');
              setIsStreamView(false);
              setStreamError(false);
            }}>
            <div className="mb-1">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                />
                <line x1="9" y1="3" x2="9" y2="21" stroke="white" strokeWidth="2" />
                <line x1="15" y1="3" x2="15" y2="21" stroke="white" strokeWidth="2" />
                <line x1="3" y1="9" x2="21" y2="9" stroke="white" strokeWidth="2" />
                <line x1="3" y1="15" x2="21" y2="15" stroke="white" strokeWidth="2" />
              </svg>
            </div>
            <div className="text-white fs-8">{t('tracker')}</div>
          </div>

          <div
            className={`flex-fill d-flex flex-column align-items-center justify-content-center position-relative cursor-pointer ${currentView === 'statistics' ? 'bg-primary' : 'bg-dark'
              }`}
            onClick={() => {
              setCurrentView('statistics');
              setIsStreamView(false);
              setStreamError(false);
            }}>
            <div className="mb-1">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="17" width="4" height="4" fill="white" />
                <rect x="10" y="13" width="4" height="8" fill="white" />
                <rect x="17" y="9" width="4" height="12" fill="white" />
              </svg>
            </div>
            <div className="text-white fs-8">{t('statistics')}</div>
          </div>

          {isBetBuilderAvailable() && (
            <div
              className={`flex-fill d-flex flex-column align-items-center justify-content-center position-relative cursor-pointer ${betBuilder.isEnabled ? 'bg-primary' : 'bg-dark'
                }`}
              onClick={handleBetBuilderToggle}
              style={{
                borderRight: streamAvailable ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
              }}>
              <div className="mb-1">
                <BetBuilderIcon size={18} style={{ marginLeft: '0' }} firstBColor="#FFFFFF" />
              </div>
              <div className="text-white fs-8">{t('betBuilder.title')}</div>
            </div>
          )}

          {streamAvailable && (
            <div
              className={`flex-fill d-flex flex-column align-items-center justify-content-center position-relative cursor-pointer ${currentView === 'stream' ? 'bg-primary' : 'bg-dark'
                }`}
              onClick={() => {
                setCurrentView('stream');
                setIsStreamView(true);
              }}>
              <div className="mb-1 position-relative">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="2"
                    y="3"
                    width="20"
                    height="14"
                    rx="2"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                  />
                  <polyline points="22,17 12,21 2,17" stroke="white" strokeWidth="2" fill="none" />
                  <polygon points="10,8 14,10 10,12" fill="white" />
                </svg>
                <div
                  className="position-absolute"
                  style={{
                    top: '-4px',
                    right: '-2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#ff4444',
                    borderRadius: '50%',
                    border: '1px solid white'
                  }}
                />
              </div>
              <div className="text-white fs-8">{t('liveTv')}</div>
            </div>
          )}
        </div>

        {currentView === 'tracker' ? (
          <MatchTracker
            matchId={match.providerId.split(':')[2]}
            language={getLanguageCode().split('-')[0]}
            isDesktop={!!matchDetailId}
            selectedTabCallbackFunc={setMatchtrackerCallback}
          />
        ) : currentView === 'statistics' ? (
          <Statistics
            matchId={match.providerId.split(':')[2]}
            language={getLanguageCode().split('-')[0]}
            isDesktop={!!matchDetailId}
          />
        ) : (
          <MatchStreamer
            matchId={match.providerId.split(':')[2]}
            streamUrl={streamUrl}
            fullscreenUrl={fullscreenUrl}
            isVisible={currentView === 'stream'}
          />
        )}
      </div>
      <div className="overflow-x-auto overflow-y-hidden text-nowrap mb-2 bg-dark py-2 fs-7 d-flex align-items-center">
        {/* Bet Builder Mode Indicator */}
        {betBuilder.isEnabled && isBetBuilderAvailable() && (
          <div className="bet-builder-indicator d-inline-flex align-items-center me-2 px-2">
            <span className="badge bg-success">
              <BetBuilderIcon size={14} style={{ marginLeft: '0', marginRight: '4px' }} firstBColor="#FFFFFF" />
              {t('betBuilder.title', 'Bet Builder')} Mode
            </span>
          </div>
        )}
        {availableMarketGroups &&
          availableMarketGroups.map((market) => {
            return (
              <div
                key={market}
                className={`d-inline text-white text-center py-2 px-3 ${selectedMarket === market ? 'border-primary' : 'border-transparent'
                  }`}
                onClick={() => setSelectedMarket(market)}
                style={{
                  cursor: 'pointer',
                  borderBottom: '3px solid transparent'
                }}>
                {t(`betGroup.${market}`)}
              </div>
            );
          })}
      </div>
      <div className={'container pe-1 ps-1'}>
        {availableMarketGroups && getMarkets(selectedMarket)}
      </div>
      {/* Bet Builder Modal */}
      {isBetBuilderAvailable() && <BetBuilderModal />}
    </>
  );
}
export default MatchDetail;
